import { useQuery } from "@tanstack/react-query";
import { Clock, Disc, Heart, ListMusic, Loader, Music2, PlusCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { ProviderTabs } from "../components/ProviderTabs";
import { TrackList } from "../components/TrackList";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { usePlaylistStore } from "../stores/playlistStore";
import { useRecentlyPlayedStore } from "../stores/recentlyPlayedStore";
import { Track } from "../types";

type ContentType = "all" | "tracks" | "albums" | "playlists" | "liked" | "recent" | "local";

export const Library = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [contentType, setContentType] = useState<ContentType>("recent"); // Changed default to recent
  const { isAuthenticated, login } = useSpotifyAuth();
  const { getAllAlbums, getLikedContent, initialized: libraryInitialized, getAllTracks } = useLocalLibraryStore();
  const { files, addFile, removeFile, cleanup, loadFilesFromDB, initialized: filesInitialized } = useLocalFilesStore();
  const { playlists } = usePlaylistStore();
  const { getRecentTracks } = useRecentlyPlayedStore();
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Spotify queries
  const { data: spotifyTracks, isLoading: isLoadingTracks } = useQuery({
    queryKey: ["saved-tracks"],
    queryFn: () => spotifyApi.currentUser.tracks.savedTracks(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const { data: spotifyPlaylists, isLoading: isLoadingPlaylists } = useQuery({
    queryKey: ["playlists"],
    queryFn: () => spotifyApi.currentUser.playlists.playlists(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const { data: spotifyAlbums, isLoading: isLoadingAlbums } = useQuery({
    queryKey: ["saved-albums"],
    queryFn: () => spotifyApi.currentUser.albums.savedAlbums(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const { data: recentlyPlayed, isLoading: isLoadingRecent } = useQuery({
    queryKey: ["recently-played"],
    queryFn: () => spotifyApi.player.getRecentlyPlayedTracks(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const isSpotifyLoading = isLoadingTracks || isLoadingPlaylists || isLoadingAlbums || isLoadingRecent;

  // Load files when component mounts if not initialized
  useEffect(() => {
    const initializeFiles = async () => {
      if (!filesInitialized) {
        setIsLoading(true);
        try {
          await loadFilesFromDB();
        } catch (err) {
          console.error("Error loading files:", err);
          setError("Failed to load local files");
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    initializeFiles();
  }, [filesInitialized, loadFilesFromDB]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;

      setIsLoading(true);
      setError(null);

      try {
        const filePromises = Array.from(e.target.files).map((file) => addFile(file));
        await Promise.all(filePromises);
      } catch (err) {
        console.error("Error processing files:", err);
        setError("Failed to process audio files. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [addFile]
  );

  // Get local content
  const localAlbums = getAllAlbums();
  const allTracks = [...getAllTracks(), ...files];
  const uniqueTracks = Array.from(new Map(allTracks.map((track) => [track.id, track])).values());
  const likedTracks = getLikedContent("track")
    .filter((item) => item.source === "local")
    .map((item) => uniqueTracks.find((track) => track.id === item.id))
    .filter(Boolean) as Track[];
  const recentTracks = getRecentTracks();

  // Add this effect after other useEffects
  useEffect(() => {
    if (contentType === "recent" && recentTracks.length === 0) {
      setContentType("tracks");
    }
  }, [contentType, recentTracks.length]);

  if (isLoading || isSpotifyLoading || !libraryInitialized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader className="w-8 h-8 animate-spin text-yellow-500" />
        <p className="text-zinc-400 mt-4">Loading library...</p>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={PlusCircle}
        title="Error loading library"
        description={error}
        action={{
          label: "Try Again",
          onClick: () => loadFilesFromDB(),
        }}
      />
    );
  }

  const renderContent = () => {
    if (activeProvider === "spotify") {
      const spotifyContent = {
        tracks:
          spotifyTracks?.items.map((item) => ({
            id: item.track.id,
            title: item.track.name,
            artist: item.track.artists[0].name,
            albumArt: item.track.album.images[0]?.url,
            duration: item.track.duration_ms / 1000,
            url: item.track.preview_url || "",
            source: "spotify" as const,
            spotifyData: item.track,
          })) || [],
        albums:
          spotifyAlbums?.items.map((item) => ({
            id: item.album.id,
            name: item.album.name,
            artist: item.album.artists[0].name,
            coverUrl: item.album.images[0]?.url,
            trackCount: item.album.total_tracks,
            source: "spotify" as const,
            type: "album" as const,
          })) || [],
        playlists:
          spotifyPlaylists?.items.map((playlist) => ({
            id: playlist.id,
            name: playlist.name,
            artist: playlist.owner.display_name || "Unknown",
            coverUrl: playlist.images[0]?.url,
            trackCount: playlist.tracks?.total ?? 0,
            source: "spotify" as const,
            type: "playlist" as const,
          })) || [],
        recent:
          recentlyPlayed?.items.map((item) => ({
            id: item.track.id,
            title: item.track.name,
            artist: item.track.artists[0].name,
            albumArt: item.track.album.images[0]?.url,
            duration: item.track.duration_ms / 1000,
            url: item.track.preview_url || "",
            source: "spotify" as const,
            spotifyData: item.track,
          })) || [],
      };

      const hasContent = Object.values(spotifyContent).some((arr) => arr.length > 0);

      if (!hasContent) {
        return (
          <EmptyState
            icon={PlusCircle}
            title="Your Spotify library is empty"
            description="Start adding music to your Spotify library to see it here"
            action={{
              label: "Browse Music",
              href: "/search",
            }}
          />
        );
      }

      switch (contentType) {
        case "tracks":
          return spotifyContent.tracks.length ? (
            <TrackList
              tracks={spotifyContent.tracks}
              onPlay={(track) => {
                setCurrentTrack(track);
                setIsPlaying(true);
              }}
            />
          ) : (
            <EmptyState
              icon={Music2}
              title="No saved tracks"
              description="Save some tracks from Spotify to see them here"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        case "albums":
          return spotifyContent.albums.length ? (
            <MediaGrid items={spotifyContent.albums} />
          ) : (
            <EmptyState
              icon={Disc}
              title="No saved albums"
              description="Save some albums from Spotify to see them here"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        case "playlists":
          return spotifyContent.playlists.length ? (
            <MediaGrid items={spotifyContent.playlists} />
          ) : (
            <EmptyState
              icon={ListMusic}
              title="No playlists"
              description="Create some playlists to see them here"
              action={{
                label: "Create Playlist",
                href: "/playlists",
              }}
            />
          );

        case "recent":
          return spotifyContent.recent.length ? (
            <TrackList
              tracks={spotifyContent.recent}
              onPlay={(track) => {
                setCurrentTrack(track);
                setIsPlaying(true);
              }}
            />
          ) : (
            <EmptyState
              icon={Clock}
              title="No recent tracks"
              description="Start listening to see your history"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        default:
          return (
            <div className="space-y-8">
              {spotifyContent.tracks.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Saved Tracks</h2>
                  <TrackList
                    tracks={spotifyContent.tracks.slice(0, 5)}
                    onPlay={(track) => {
                      setCurrentTrack(track);
                      setIsPlaying(true);
                    }}
                  />
                </section>
              )}

              {spotifyContent.albums.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Your Albums</h2>
                  <MediaGrid items={spotifyContent.albums.slice(0, 6)} />
                </section>
              )}

              {spotifyContent.playlists.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Your Playlists</h2>
                  <MediaGrid items={spotifyContent.playlists.slice(0, 6)} />
                </section>
              )}

              {spotifyContent.recent.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Recently Played</h2>
                  <TrackList
                    tracks={spotifyContent.recent.slice(0, 5)}
                    onPlay={(track) => {
                      setCurrentTrack(track);
                      setIsPlaying(true);
                    }}
                  />
                </section>
              )}
            </div>
          );
      }
    }

    // Local content
    const localContent = {
      tracks: uniqueTracks,
      albums: localAlbums.map((album) => ({
        id: album.id,
        name: album.name,
        artist: album.tracks[0]?.artist || "Various Artists",
        coverUrl: album.coverUrl || album.tracks[0]?.albumArt,
        trackCount: album.tracks.length,
        source: "local" as const,
        type: "album" as const,
      })),
      playlists: playlists.map((playlist) => ({
        id: playlist.id,
        name: playlist.name,
        artist: "Your Playlist",
        coverUrl: playlist.icon,
        trackCount: playlist.tracks.length,
        source: "local" as const,
        type: "playlist" as const,
      })),
      liked: likedTracks,
      recent: recentTracks,
      local: files,
    };

    const hasContent = Object.values(localContent).some((arr) => arr.length > 0);

    if (!hasContent) {
      return (
        <EmptyState
          icon={PlusCircle}
          title="Your library is empty"
          description="Start adding music to your library to see it here"
          action={{
            label: "Upload Music",
            onClick: () => document.getElementById("file-upload")?.click(),
          }}
        />
      );
    }

    switch (contentType) {
      case "tracks":
        return localContent.tracks.length ? (
          <TrackList
            tracks={localContent.tracks}
            onPlay={(track) => {
              setCurrentTrack(track);
              setIsPlaying(true);
            }}
          />
        ) : (
          <EmptyState
            icon={Music2}
            title="No local tracks"
            description="Add some local music files to get started"
            action={{
              label: "Upload Music",
              onClick: () => document.getElementById("file-upload")?.click(),
            }}
          />
        );

      case "albums":
        return localContent.albums.length ? (
          <MediaGrid items={localContent.albums} />
        ) : (
          <EmptyState
            icon={Disc}
            title="No albums"
            description="Create some albums to organize your music"
            action={{
              label: "Create Album",
              href: "/albums",
            }}
          />
        );

      case "playlists":
        return localContent.playlists.length ? (
          <MediaGrid items={localContent.playlists} />
        ) : (
          <EmptyState
            icon={ListMusic}
            title="No playlists"
            description="Create some playlists to organize your music"
            action={{
              label: "Create Playlist",
              href: "/playlists",
            }}
          />
        );

      case "liked":
        return localContent.liked.length ? (
          <TrackList
            tracks={localContent.liked}
            onPlay={(track) => {
              setCurrentTrack(track);
              setIsPlaying(true);
            }}
          />
        ) : (
          <EmptyState
            icon={Heart}
            title="No liked songs"
            description="Like some songs to see them here"
            action={{
              label: "Browse Music",
              href: "/search",
            }}
          />
        );

      case "recent":
        return localContent.recent.length ? (
          <TrackList
            tracks={localContent.recent}
            onPlay={(track) => {
              setCurrentTrack(track);
              setIsPlaying(true);
            }}
          />
        ) : (
          <EmptyState
            icon={Clock}
            title="No recent tracks"
            description="Start listening to see your history"
            action={{
              label: "Browse Music",
              href: "/search",
            }}
          />
        );

      case "local":
        return localContent.local.length ? (
          <TrackList
            tracks={localContent.local}
            onPlay={(track) => {
              setCurrentTrack(track);
              setIsPlaying(true);
            }}
            onRemove={removeFile}
            showLocalControls
          />
        ) : (
          <EmptyState
            icon={Music2}
            title="No local files"
            description="Upload your music files to play them offline"
            action={{
              label: "Upload Files",
              onClick: () => document.getElementById("file-upload")?.click(),
            }}
          />
        );

      default:
        return (
          <div className="space-y-8">
            {localContent.tracks.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">All Tracks</h2>
                <TrackList
                  tracks={localContent.tracks.slice(0, 5)}
                  onPlay={(track) => {
                    setCurrentTrack(track);
                    setIsPlaying(true);
                  }}
                />
              </section>
            )}

            {localContent.albums.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Your Albums</h2>
                <MediaGrid items={localContent.albums.slice(0, 6)} />
              </section>
            )}

            {localContent.playlists.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Your Playlists</h2>
                <MediaGrid items={localContent.playlists.slice(0, 6)} />
              </section>
            )}

            {localContent.liked.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Liked Songs</h2>
                <TrackList
                  tracks={localContent.liked.slice(0, 5)}
                  onPlay={(track) => {
                    setCurrentTrack(track);
                    setIsPlaying(true);
                  }}
                />
              </section>
            )}

            {localContent.recent.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Recently Played</h2>
                <TrackList
                  tracks={localContent.recent.slice(0, 5)}
                  onPlay={(track) => {
                    setCurrentTrack(track);
                    setIsPlaying(true);
                  }}
                />
              </section>
            )}

            {localContent.local.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Local Files</h2>
                <TrackList
                  tracks={localContent.local.slice(0, 5)}
                  onPlay={(track) => {
                    setCurrentTrack(track);
                    setIsPlaying(true);
                  }}
                  onRemove={removeFile}
                  showLocalControls
                />
              </section>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Your Library</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setContentType("recent")}
                className={`text-sm ${contentType === "recent" ? "text-white" : "text-zinc-400"}`}
              >
                Recent
              </button>
              <button
                onClick={() => setContentType("tracks")}
                className={`text-sm ${contentType === "tracks" ? "text-white" : "text-zinc-400"}`}
              >
                Tracks
              </button>
              <button
                onClick={() => setContentType("albums")}
                className={`text-sm ${contentType === "albums" ? "text-white" : "text-zinc-400"}`}
              >
                Albums
              </button>
              <button
                onClick={() => setContentType("playlists")}
                className={`text-sm ${contentType === "playlists" ? "text-white" : "text-zinc-400"}`}
              >
                Playlists
              </button>
              {activeProvider === "local" && (
                <button
                  onClick={() => setContentType("liked")}
                  className={`text-sm ${contentType === "liked" ? "text-white" : "text-zinc-400"}`}
                >
                  Liked
                </button>
              )}
              {activeProvider === "local" && (
                <button
                  onClick={() => setContentType("local")}
                  className={`text-sm ${contentType === "local" ? "text-white" : "text-zinc-400"}`}
                >
                  Local Files
                </button>
              )}
              <button
                onClick={() => setContentType("all")}
                className={`text-sm ${contentType === "all" ? "text-white" : "text-zinc-400"}`}
              >
                All
              </button>
            </div>
          </div>
        </div>

        <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

        {renderContent()}
      </div>
    </div>
  );
};
